import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'

export type ApiTarget = {
  label: string
  url: string
}

type ApiBaseContextValue = {
  baseUrl: string
  isRemote: boolean
  savedTargets: ApiTarget[]
  setBaseUrl: (url: string) => void
  addTarget: (target: ApiTarget) => void
  removeTarget: (url: string) => void
  /** Auth token for the current remote target */
  remoteToken: string | null
  /** Set auth token for current remote target */
  setRemoteToken: (token: string | null) => void
  /** Whether a remote login is needed (set by 401 interceptor) */
  remoteLoginNeeded: boolean
  setRemoteLoginNeeded: (v: boolean) => void
}

const STORAGE_KEY = 'wistor-api-base'
const TARGETS_KEY = 'wistor-api-targets'

const DEFAULT_TARGETS: ApiTarget[] = [
  { label: 'Local (same-origin)', url: '' },
]

function loadBaseUrl(): string {
  try { return localStorage.getItem(STORAGE_KEY) ?? '' } catch { return '' }
}

function loadTargets(): ApiTarget[] {
  try {
    const raw = localStorage.getItem(TARGETS_KEY)
    if (!raw) return DEFAULT_TARGETS
    const parsed = JSON.parse(raw) as ApiTarget[]
    if (!parsed.some(t => t.url === '')) return [DEFAULT_TARGETS[0], ...parsed]
    return parsed
  } catch { return DEFAULT_TARGETS }
}

const ApiBaseContext = createContext<ApiBaseContextValue | null>(null)

export function ApiBaseProvider({ children }: { children: React.ReactNode }) {
  const [baseUrl, setBaseUrlState] = useState(loadBaseUrl)
  const [savedTargets, setSavedTargets] = useState(loadTargets)

  // Add inside ApiBaseProvider, after the existing useState calls:
  const [remoteToken, setRemoteTokenState] = useState<string | null>(() => {
    try {
      const base = loadBaseUrl()
      if (!base) return null
      return localStorage.getItem(`wistor-remote-token:${base}`) ?? null
    } catch { return null }
  })
  const [remoteLoginNeeded, setRemoteLoginNeeded] = useState(false)

  const setRemoteToken = useCallback((token: string | null) => {
    setRemoteTokenState(token)
    try {
      const key = `wistor-remote-token:${baseUrl}`
      if (token) localStorage.setItem(key, token)
      else localStorage.removeItem(key)
    } catch {}
  }, [baseUrl])

  // When baseUrl changes, load the token for that target
  React.useEffect(() => {
    if (!baseUrl) {
      setRemoteTokenState(null)
      setRemoteLoginNeeded(false)
      return
    }
    try {
      const saved = localStorage.getItem(`wistor-remote-token:${baseUrl}`)
      setRemoteTokenState(saved)
      setRemoteLoginNeeded(!saved)
    } catch {
      setRemoteTokenState(null)
      setRemoteLoginNeeded(true)
    }
  }, [baseUrl])

  const setBaseUrl = useCallback((url: string) => {
    const cleaned = url.replace(/\/+$/, '')
    setBaseUrlState(cleaned)
    try { localStorage.setItem(STORAGE_KEY, cleaned) } catch {}
  }, [])

  const addTarget = useCallback((target: ApiTarget) => {
    setSavedTargets(prev => {
      const cleaned = { ...target, url: target.url.replace(/\/+$/, '') }
      if (prev.some(t => t.url === cleaned.url)) return prev
      const next = [...prev, cleaned]
      try { localStorage.setItem(TARGETS_KEY, JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

  const removeTarget = useCallback((url: string) => {
    if (url === '') return
    setSavedTargets(prev => {
      const next = prev.filter(t => t.url !== url)
      try { localStorage.setItem(TARGETS_KEY, JSON.stringify(next)) } catch {}
      return next
    })
  }, [])

// Add to the value memo:
  const value = useMemo<ApiBaseContextValue>(() => ({
    baseUrl, isRemote: baseUrl !== '', savedTargets,
    setBaseUrl, addTarget, removeTarget,
    remoteToken, setRemoteToken,
    remoteLoginNeeded, setRemoteLoginNeeded,
  }), [baseUrl, savedTargets, setBaseUrl, addTarget, removeTarget,
    remoteToken, setRemoteToken, remoteLoginNeeded, setRemoteLoginNeeded])

  return (
    <ApiBaseContext.Provider value={value}>{children}</ApiBaseContext.Provider>
  )
}

export function useApiBase(): ApiBaseContextValue {
  const ctx = useContext(ApiBaseContext)
  if (!ctx) throw new Error('useApiBase must be used within <ApiBaseProvider>')
  return ctx
}
